/**
 * NodePalette -- Left sidebar for the workflow builder.
 *
 * Draggable node palette organized by category from NODE_TYPE_CONFIG.
 * Categories are collapsible. Nodes can be dragged onto the canvas
 * (onDragStart sets transfer data for the drop handler) OR clicked to
 * add at canvas centre via `onAdd`. Click-to-add is the default for
 * keyboard / touch users; drag-to-position remains for power users.
 */

import { useState, useMemo } from 'react';
import {
  Zap, Plug, GitBranch, GitMerge, ShieldCheck, Repeat, Clock, Bell, Globe, Sparkles,
  Bot, ChevronRight, Search, Variable, Code, Workflow, Route, Filter, Sigma,
  UnfoldVertical, ArrowUpDown, ListEnd, CopyMinus, ShieldAlert, Octagon,
  Hourglass, Reply, CalendarClock, FileText, Braces, Send, Database,
  Radio,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  NODE_TYPE_CONFIG,
  getNodeRuntimeSupport,
  type NodeType,
} from '@/lib/mock-automate-data';
import { Input } from '@/components/ui/input';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';

// ── Icon map ─────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  zap: Zap, bot: Bot, plug: Plug, 'git-branch': GitBranch, 'git-merge': GitMerge,
  sparkles: Sparkles, 'shield-check': ShieldCheck, repeat: Repeat, clock: Clock,
  globe: Globe, bell: Bell, search: Search, variable: Variable, code: Code,
  workflow: Workflow, route: Route, filter: Filter, sigma: Sigma,
  'unfold-vertical': UnfoldVertical, 'arrow-up-down': ArrowUpDown,
  'list-end': ListEnd, 'copy-minus': CopyMinus, 'shield-alert': ShieldAlert,
  octagon: Octagon, hourglass: Hourglass, reply: Reply,
  'calendar-clock': CalendarClock, 'file-text': FileText, braces: Braces, send: Send,
  radio: Radio,
};

// ── Category ordering ────────────────────────────────────────────────────

const CATEGORY_ORDER = ['Triggers', 'AI & Agents', 'Logic', 'Data', 'Transform', 'Actions', 'Governance', 'Automation'];

// ── Component ────────────────────────────────────────────────────────────

export interface NodePaletteProps {
  /**
   * Fires when the user clicks (or activates via keyboard) a palette
   * item. The builder is responsible for placing the node on the canvas.
   * Optional — if omitted, palette stays drag-only.
   */
  onAdd?: (nodeType: NodeType) => void;
}

export function NodePalette({ onAdd }: NodePaletteProps = {}) {
  const [search, setSearch] = useState('');
  const [openCategories, setOpenCategories] = useState<Set<string>>(
    () => new Set(CATEGORY_ORDER),
  );

  // Group node types by category
  const grouped = useMemo(() => {
    const map = new Map<string, { type: NodeType; label: string; icon: string; color: string; support: ReturnType<typeof getNodeRuntimeSupport> }[]>();
    for (const [key, cfg] of Object.entries(NODE_TYPE_CONFIG)) {
      const nodeType = key as NodeType;
      if (search && !cfg.label.toLowerCase().includes(search.toLowerCase())) continue;
      const arr = map.get(cfg.category) ?? [];
      arr.push({
        type: nodeType,
        label: cfg.label,
        icon: cfg.icon,
        color: cfg.color,
        support: getNodeRuntimeSupport(nodeType),
      });
      map.set(cfg.category, arr);
    }
    return map;
  }, [search]);

  const toggleCategory = (cat: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const onDragStart = (e: React.DragEvent, nodeType: NodeType) => {
    e.dataTransfer.setData('application/reactflow-type', nodeType);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="w-[180px] flex flex-col border-r bg-muted/30 h-full">
      {/* Search */}
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            placeholder="Filter nodes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 pl-7 text-xs"
          />
        </div>
      </div>

      {/* Categories */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {CATEGORY_ORDER.map((category) => {
            const items = grouped.get(category);
            if (!items || items.length === 0) return null;
            const isOpen = openCategories.has(category);

            return (
              <Collapsible
                key={category}
                open={isOpen}
                onOpenChange={() => toggleCategory(category)}
              >
                <CollapsibleTrigger className="flex items-center gap-1 w-full px-1 py-1 text-[10px] font-semibold uppercase text-muted-foreground hover:text-foreground transition-colors">
                  <ChevronRight
                    className={cn(
                      'h-3 w-3 transition-transform',
                      isOpen && 'rotate-90',
                    )}
                  />
                  {category}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-0.5 ml-1">
                    {items.map((item) => {
                      const Icon = ICON_MAP[item.icon] ?? Zap;
                      const isUnsupported = item.support.status === 'unsupported';
                      const tooltip = onAdd
                        ? isUnsupported
                          ? `${item.label} is not wired for V1 runtime yet`
                          : `Click to add or drag to position the ${item.label} node`
                        : `Drag to add ${item.label} node`;
                      return (
                        <button
                          key={item.type}
                          type="button"
                          draggable={!isUnsupported}
                          disabled={isUnsupported}
                          onDragStart={(e) => {
                            if (isUnsupported) return;
                            onDragStart(e, item.type);
                          }}
                          onClick={() => {
                            if (isUnsupported) return;
                            onAdd?.(item.type);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (isUnsupported) return;
                              onAdd?.(item.type);
                            }
                          }}
                          aria-label={tooltip}
                          className={cn(
                            'flex items-center gap-2 px-2 py-1.5 rounded-md w-full text-left',
                            'transition-colors group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                            isUnsupported
                              ? 'opacity-55 cursor-not-allowed'
                              : onAdd
                                ? 'cursor-pointer hover:bg-accent/60 active:bg-accent'
                                : 'cursor-grab active:cursor-grabbing hover:bg-accent/50',
                          )}
                          title={tooltip}
                        >
                          <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded bg-muted/80">
                            <Icon className={cn('h-3 w-3', item.color)} />
                          </div>
                          <span className="text-xs font-medium truncate flex-1">{item.label}</span>
                          {item.support.status !== 'ready' && (
                            <span className="text-[8px] text-muted-foreground shrink-0">
                              {item.support.label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}

          {grouped.size === 0 && (
            <p className="text-[10px] text-muted-foreground text-center py-4">No matching nodes</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
